
import { OC_PLUS_LOGO } from '@marketplace/config/ocplus.js'
import {
    CALCULATE_MARGIN,
    DISCOUNTED_PRICE_CALCULATOR,
} from '@marketplace/mixins/helpers.js'

export default {
    name: 'PriceAndDiscountsWrapper',

    props: {
        price: {
            type: Number,
            default: null,
        },
        msrpPrice: {
            type: Number,
            default: null,
        },
        quantity: {
            type: Number,
            default: 1,
        },
        discounts: {
            type: Object,
            default: null,
        },
        requestCallback: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            OC_PLUS_LOGO,
        }
    },

    computed: {
        /**
         * Checks if the discounts object has anything inside it
         * @returns {boolean}
         */
        hasAvailableDiscounts() {
            if (!this.discounts) return false
            return Object.keys(this.discounts)?.length > 0 || false
        },
        /**
         * Checks if the there are any bulk discounts
         * @returns {boolean}
         */
        hasAnyBulkDiscounts() {
            if (!this.hasAvailableDiscounts) return false

            return (
                Object.keys(this.discounts?.bulkDiscounts)?.length > 0 || false
            )
        },
        /**
         * Aggregates a bunch of product discounts looking for the highest %
         * Returns the best discount available
         * @returns {object}
         */
        productDiscount() {
            const {
                campaignDiscount = {},
                globalDiscount = {},
                loyaltyDiscount = {},
            } = this.discounts

            const highestPercentageDiscount = [
                campaignDiscount,
                loyaltyDiscount,
                globalDiscount,
            ].reduce((previousDiscount, nextDiscount) => {
                return previousDiscount &&
                    previousDiscount.percentage > nextDiscount.percentage
                    ? previousDiscount
                    : nextDiscount
            }, {})

            if (!Object.keys(highestPercentageDiscount).length) return {}

            const selectedDiscountKey = Object.keys(
                highestPercentageDiscount
            )[0]

            return highestPercentageDiscount[selectedDiscountKey]
        },
        /**
         * Finds the highest possible existing bulk discount and returns an object to fake some prices that will suggest the highest discount
         * @returns {object}
         */
        topBulkDiscount() {
            if (!this.hasAnyBulkDiscounts) return null

            const { bulkDiscounts } = this.discounts
            const topDiscountKey = Math.max(...Object.keys(bulkDiscounts))
            const topBulkDiscountObject = bulkDiscounts[topDiscountKey]
            const lowestPricePossible = this.calculateDiscountedPrice(
                this.price,
                topBulkDiscountObject.percentage
            )

            return {
                discount: topBulkDiscountObject,
                lowestPricePossible,
                message: this.$t('cart.messages.special_discount.body.BULK', [
                    topBulkDiscountObject.minimumQuantity,
                    topBulkDiscountObject.percentage,
                ]),
            }
        },
        /**
         * Filters down the list of bulk discounts. If there is a discount with a higher % than the bulk discount then that bulk discount is removed from the list
         * @returns {boolean}
         */
        availableBulkDiscounts() {
            const globalDiscountPercentage =
                this.productDiscount?.percentage || 0
            if (!this.hasAnyBulkDiscounts) return []

            return Object.entries(this.discounts?.bulkDiscounts)
                ?.filter((discount) => {
                    const percentage = parseInt(discount?.[0]) || 0
                    return globalDiscountPercentage < percentage
                })
                ?.map((item) => item[1])
        },
        /**
         * Checks for the highest order discount.
         * Compares it to the other discounts. If higher will return an object
         * @returns {object}
         */
        topOrderDiscount() {
            const { orderDiscount } = this.discounts

            // Convert discount keys to an array of discount objects and sort them by minimumOrderValue
            const eligibleDiscounts = Object.values(orderDiscount).sort(
                (a, b) => a.minimumOrderValue - b.minimumOrderValue
            )

            // Map the sorted discounts to messages
            const discountMessages = eligibleDiscounts.map((discount) => {
                return this.$t(
                    'cart.messages.special_discount.body.ORDER_DISCOUNT',
                    [
                        discount.percentage,
                        this.$n(
                            discount.minimumOrderValue,
                            'currency-no-decimal'
                        ),
                    ]
                )
            })

            return discountMessages
        },

        /**
         * Checks between all the discounts and bulk discounts which one is currently active and has the highest discount
         * @returns {object}
         */
        bestAvailableDiscount() {
            let activeBulkDiscounts = []
            if (this.hasAnyBulkDiscounts > 0) {
                activeBulkDiscounts = Object.values(this.availableBulkDiscounts)
                    .sort((a, b) => {
                        return b.minimumQuantity - a.minimumQuantity
                    })
                    .filter((discount) => {
                        return this.quantity >= discount.minimumQuantity
                    })?.[0]
            }

            const bestActiveDiscount = [
                this.productDiscount,
                activeBulkDiscounts,
            ].sort((a, b) => {
                const percentageA = a?.percentage || 0
                const percentageB = b?.percentage || 0
                return percentageB - percentageA
            })

            return bestActiveDiscount[0]
        },
        /**
         * Returns the percentage (Number) of the best currently active discount
         * @returns {number}
         */
        discountPercentage() {
            return this.bestAvailableDiscount?.percentage || 0
        },
        /**
         * Figures out what to tell the user.
         * Message depends on event type, discount type, and if its a bulk or not
         * @returns {string}
         */
        discountTypeMessage() {
            const {
                customerGroupType = '',
                campaign = '',
                isBulkDiscount = false,
                minimumQuantity = 0,
                orderchampContributionPercentage = 0,
            } = this.bestAvailableDiscount

            let type = campaign || customerGroupType || ''

            if (isBulkDiscount) {
                return this.$t('cart.messages.special_discount.body.BULK', [
                    minimumQuantity,
                    this.discountPercentage,
                ])
            }

            if (type.includes('orderchamp-plus-')) {
                type = 'orderchamp-plus'
            }

            const translationKey = `cart.messages.special_discount.body.${type}`

            // We consider loyaltyDiscount as having OC+ new storefront user discount
            if (type === 'digital_fair' && this.hasLoyaltyDiscount) {
                return this.$t('text.odf_and_oc_plus_discount_new', {
                    discount: this.$n(this.discountPercentage / 100, 'percent'),
                    ocPlusLogo: OC_PLUS_LOGO.bold.dark,
                    ocPlusPercentage: orderchampContributionPercentage || 5,
                })
            }

            if (
                this.hasLoyaltyDiscount &&
                this.bestAvailableDiscount.percentage >
                    Object.values(this.discounts.loyaltyDiscount)[0]?.percentage
            ) {
                if (type === 'connect_and_grow') {
                    return this.$t(
                        'text.connect_and_grow_discount_and_oc_plus_discount_new',
                        {
                            discount: this.$n(
                                this.discountPercentage / 100,
                                'percent'
                            ),
                            ocPlusLogo: OC_PLUS_LOGO.bold.dark,
                            ocPlusPercentage:
                                orderchampContributionPercentage || 10,
                        }
                    )
                }
                return this.$t('text.discount_and_oc_plus_discount_new', {
                    discount: this.$n(this.discountPercentage / 100, 'percent'),
                    ocPlusLogo: OC_PLUS_LOGO.bold.dark,
                    ocPlusPercentage: orderchampContributionPercentage || 5,
                })
            }

            if (this.hasLoyaltyDiscount) {
                return this.$t(
                    'cart.messages.special_discount.body.orderchamp_plus_subscription_base',
                    {
                        discount: this.$n(
                            this.discountPercentage / 100,
                            'percent'
                        ),
                        ocPlusLogo: OC_PLUS_LOGO.bold.dark,
                    }
                )
            }

            return this.$te(translationKey)
                ? `${this.$t(translationKey, {
                      discount: this.$n(
                          this.discountPercentage / 100,
                          'percent'
                      ),
                  })}`
                : `no translation key. Add: ${translationKey}`
        },
        /**
         * Calculated the discounted price from the original price - the discount %
         * @returns {number}
         */
        discountedPrice() {
            return this.calculateDiscountedPriceWithQuantity(
                this.price,
                this.discountPercentage,
                this.quantity
            )
        },
        /**
         * Calculated the discounted UNIT price from the original price - the discount %
         * @returns {number}
         */
        discountedUnitPrice() {
            return this.calculateDiscountedPriceWithQuantity(
                this.price,
                this.discountPercentage,
                1
            )
        },
        /**
         * Takes the MSRP and current discountedPrice (or originalPrice) and returns the margin
         * includes the 'x'
         * @returns {string}
         */
        margin() {
            return this.calculateMargin(
                this.msrpPrice,
                this.discountedUnitPrice
            )
        },
        /**
         * Emitted to children. Stores info about the best discount that is in use
         * @returns {object}
         */
        discountsPayload() {
            if (this.discountPercentage <= 0) return null
            const isBulkDiscount =
                this.bestAvailableDiscount?.isBulkDiscount || false
            const isOrderchampPlus = this.hasLoyaltyDiscount

            return {
                discount: this.bestAvailableDiscount,
                percentage: this.discountPercentage,
                message: this.discountTypeMessage,
                isBulkDiscount,
                isOrderchampPlus,
            }
        },
        /**
         * Emitted to children. Stores info about the prices
         * @returns {object}
         */
        pricesPayload() {
            return {
                currentPrice: this.discountedPrice,
                currentUnitPrice: this.discountedUnitPrice,
                originalPrice: this.price,
                originalMsrp: this.msrpPrice,
            }
        },

        hasLoyaltyDiscount() {
            return Object.keys(this.discounts.loyaltyDiscount).length
        },
    },

    mounted() {
        this.emitDiscount()
    },

    updated() {
        if (!this.requestCallback) return
        this.emitDiscount()
    },

    methods: {
        ...DISCOUNTED_PRICE_CALCULATOR,
        ...CALCULATE_MARGIN,

        emitDiscount() {
            this.$emit('discountUpdated', this.discountsPayload)
        },
    },
}
